import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { formatOpdDateTime } from '../opd/opd.utils';
import { ReportAnalyticsGrid, ReportBarChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { lastMonthDateFilters } from './report-filters';
import { fetchInvestigationReport } from './reports.api';
import { useBillingDetailsFilterOptions, useInvestigationReport } from './reports.queries';
import type { InvestigationReportFilters, InvestigationReportRow } from './types';

const statusOptions: { value: InvestigationReportFilters['status']; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'bill-created', label: 'Bill Created' },
  { value: 'sample-collected', label: 'Sample Collected' },
  { value: 'lab-received', label: 'Lab Received' },
  { value: 'report-generated', label: 'Report Updated' },
  { value: 'report-delivered', label: 'Report Delivered' },
];
const sourceSectionOptions: { value: NonNullable<InvestigationReportFilters['sourceSection']>; label: string }[] = [
  { value: '', label: 'All Sections' },
  { value: 'INVESTIGATION', label: 'Investigation' },
  { value: 'OPD', label: 'OPD' },
  { value: 'EMG', label: 'EMG' },
  { value: 'IPD', label: 'IPD' },
  { value: 'Daycare', label: 'Daycare' },
  { value: 'Dialysis', label: 'Dialysis' },
  { value: 'OT', label: 'OT' },
];

const initialFilters = (): InvestigationReportFilters => ({
  page: 1,
  limit: 25,
  ...lastMonthDateFilters(),
  value: '',
  status: '',
  sourceSection: '',
  doctor: '',
  referral: '',
  charge: '',
  chargeCategory: '',
  chargeSubCategory: '',
});
const amount = (value: number) => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
const statusLabel = (value: string) => value.replace(/-/g, ' ');
const badgeClass = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
const doctorName = (value: string | null | undefined) => {
  const name = value?.trim().replace(/^(?:dr\.?\s*)+/i, '');
  return name && name !== '-' ? `DR. ${name}` : '-';
};
const patientDetails = (row: Pick<InvestigationReportRow, 'phone' | 'gender' | 'ageDisplay'>) =>
  `${row.phone ?? '-'} | ${row.gender ?? '-'} | ${row.ageDisplay || '-'}`;

export const InvestigationReportPage = () => {
  const [draft, setDraft] = useState<InvestigationReportFilters>(initialFilters);
  const [filters, setFilters] = useState<InvestigationReportFilters>(draft);
  const query = useInvestigationReport(filters);
  const optionsQuery = useBillingDetailsFilterOptions();
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...draft, sortBy: current.sortBy, sortOrder: current.sortOrder, page: 1 }));
  };
  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };
  const changeLimit = (limit: number) => {
    setDraft((current) => ({ ...current, limit }));
    setFilters((current) => ({ ...current, limit, page: 1 }));
  };
  const rows = query.data?.data ?? [];
  const filterOptions = useMemo(() => {
    const option = (names: string[] | undefined) => (names ?? []).map((name) => ({ value: name, label: name }));
    return {
      doctors: option(optionsQuery.data?.doctors),
      referrals: option(optionsQuery.data?.referrals),
      charges: option(optionsQuery.data?.charges),
      chargeCategories: option(optionsQuery.data?.chargeCategories),
      chargeSubCategories: option(optionsQuery.data?.chargeSubCategories),
    };
  }, [optionsQuery.data]);
  const statusChartData = useMemo<ReportChartDatum[]>(() => (query.data?.summary.byStatus ?? []).map((item, index) => ({
    name: statusLabel(item.status),
    value: item.count,
    Amount: item.amount ?? 0,
    color: reportChartColor(index),
  })), [query.data?.summary.byStatus]);
  const sectionAmountData = useMemo<ReportChartDatum[]>(() => [...(query.data?.summary.bySection ?? [])]
    .sort((left, right) => right.amount - left.amount)
    .map((item) => ({ name: item.section, Amount: item.amount })), [query.data?.summary.bySection]);
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Test report could not be loaded.';

  const exportToExcel = async () => {
    const exportRows = await fetchAllPages<InvestigationReportRow>((page, limit) => fetchInvestigationReport({ ...filters, page, limit }), 500);
    await exportRowsToExcel({
      fileName: `Test-Report-${new Date().toISOString().slice(0, 10)}`,
      title: 'Test Report',
      subtitle: filters.fromDate && filters.toDate ? `${filters.fromDate} to ${filters.toDate}` : 'All dates',
      columns: [
        { header: 'Bill ID', width: 16, map: (row) => row.billId },
        { header: 'Bill Date', width: 20, map: (row) => formatOpdDateTime(row.billDate) },
        { header: 'Patient (UHID)', width: 26, map: patientText },
        { header: 'Mobile | Gender | Age', width: 34, map: patientDetails },
        { header: 'Source', width: 16, map: (row) => row.sourceSection },
        { header: 'Doctor', width: 22, map: (row) => doctorName(row.doctorName) },
        { header: 'Charge', width: 28, map: (row) => row.chargeName },
        { header: 'Status', width: 18, map: (row) => statusLabel(row.status) },
        { header: 'Amount', width: 14, align: 'right', numFmt: '#,##0.00', map: (row) => row.amount },
        { header: 'Generated By', width: 18, map: (row) => row.generatedBy },
      ],
      rows: exportRows,
    });
  };

  return (
    <div className="page-stack report-list-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>Test Report</h1>
          <p>Test charge details by patient, source section, doctor, and report status.</p>
        </div>
      </div>

      <form className="report-list-filter-card investigation-report-filters" onSubmit={applyFilters}>
        <label>
          Section
          <select value={draft.sourceSection ?? ''} onChange={(event) => setDraft((current) => ({ ...current, sourceSection: event.target.value as InvestigationReportFilters['sourceSection'] }))}>
            {sourceSectionOptions.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label>
          Doctor
          <SearchableSelect options={filterOptions.doctors} value={draft.doctor ?? ''} placeholder="Select Doctor" clearable onChange={(value) => setDraft((current) => ({ ...current, doctor: value }))} />
        </label>
        <label>
          Referral
          <SearchableSelect options={filterOptions.referrals} value={draft.referral ?? ''} placeholder="Select Referral" clearable onChange={(value) => setDraft((current) => ({ ...current, referral: value }))} />
        </label>
        <label>
          Charge
          <SearchableSelect options={filterOptions.charges} value={draft.charge ?? ''} placeholder="Select Charge" clearable onChange={(value) => setDraft((current) => ({ ...current, charge: value }))} />
        </label>
        <label>
          Charge Category
          <SearchableSelect options={filterOptions.chargeCategories} value={draft.chargeCategory ?? ''} placeholder="Select Category" clearable onChange={(value) => setDraft((current) => ({ ...current, chargeCategory: value }))} />
        </label>
        <label>
          Charge Sub-Category
          <SearchableSelect options={filterOptions.chargeSubCategories} value={draft.chargeSubCategory ?? ''} placeholder="Select Sub-Category" clearable onChange={(value) => setDraft((current) => ({ ...current, chargeSubCategory: value }))} />
        </label>
        <label>
          From date
          <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
        </label>
        <label>
          To date
          <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
        </label>
        <label>
          Status
          <select value={draft.status ?? ''} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value as InvestigationReportFilters['status'] }))}>
            {statusOptions.map((option) => <option key={option.value || 'all'} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label>
          Search
          <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Patient, bill, charge, doctor" />
        </label>
        <div className="investigation-report-filter-actions">
          <button className="button button-primary report-list-action" type="submit"><IconSearch />Search</button>
          <button className="button button-secondary report-list-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
          <ExcelExportButton className="report-list-action" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
        </div>
      </form>

      <section className="report-summary-grid">
        <SummaryTile label="Tests" value={query.data?.summary.count ?? 0} />
        <SummaryTile label="Amount" value={amount(query.data?.summary.amount ?? 0)} />
        {(query.data?.summary.byStatus ?? []).slice(0, 4).map((item) => <SummaryTile key={item.status} label={statusLabel(item.status)} value={item.count} />)}
      </section>

      {query.isSuccess ? (
        <ReportAnalyticsGrid>
          <ReportDonutChartCard eyebrow="Status analysis" title="Test status distribution" data={statusChartData} />
          <ReportBarChartCard
            eyebrow="Section value"
            title="Test amount by section"
            data={sectionAmountData}
            bars={[{ key: 'Amount', name: 'Amount', color: '#0d6b66' }]}
            valueKind="amount"
          />
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card report-list-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load test report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title="No test rows found" message="Change filters or date range and search again." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <>
            <div className="report-table-scroll">
              <table className="report-data-table investigation-report-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <SortableTh control={tableSort} column="billId">Bill ID</SortableTh>
                    <SortableTh control={tableSort} column="billDate">Bill Date</SortableTh>
                    <SortableTh control={tableSort} column="patient">Patient (UHID)</SortableTh>
                    <SortableTh control={tableSort} column="phone">Mobile | Gender | Age</SortableTh>
                    <SortableTh control={tableSort} column="sourceSection">Source</SortableTh>
                    <SortableTh control={tableSort} column="doctor">Doctor</SortableTh>
                    <SortableTh control={tableSort} column="chargeName">Charge</SortableTh>
                    <SortableTh control={tableSort} column="status">Status</SortableTh>
                    <SortableTh control={tableSort} column="amount">Amount</SortableTh>
                    <th>Generated By</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                      <td><Link className="bill-id-link" to={`/investigation-bill/${row.sourceId}`} title="Open investigation bill"><strong>{row.billId}</strong></Link></td>
                      <td>{formatOpdDateTime(row.billDate)}</td>
                      <td>{row.patientId ? <Link className="patient-name-link" to={`/patients/${row.patientId}`} title="Open patient profile"><strong>{patientText(row)}</strong></Link> : <strong>{patientText(row)}</strong>}</td>
                      <td>{patientDetails(row)}</td>
                      <td><span className={`investigation-source-badge ${badgeClass(row.sourceSection)}`}>{row.sourceSection}</span></td>
                      <td>{doctorName(row.doctorName)}</td>
                      <td>{row.chargeName}</td>
                      <td><span className={`investigation-status-badge ${badgeClass(row.status)}`}>{statusLabel(row.status)}</span></td>
                      <td className="number">{amount(row.amount)}</td>
                      <td>{row.generatedBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

const SummaryTile = ({ label, value }: { label: string; value: string | number }) => (
  <div className="report-summary-tile">
    <span>{label}</span>
    <strong>{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</strong>
  </div>
);

const patientText = (row: Pick<InvestigationReportRow, 'patientName' | 'uhid'>) =>
  `${row.patientName}${row.uhid ? ` (${row.uhid})` : ''}`;
